"use client";

import { useTexture } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { useAtom } from "jotai";
import { easing } from "maath";
import { useEffect, useMemo, useRef, useState } from "react";
import {
    Bone,
    BoxGeometry,
    Color,
    Float32BufferAttribute,
    MeshStandardMaterial,
    Skeleton,
    SkinnedMesh,
    SRGBColorSpace,
    Uint16BufferAttribute,
    Vector3,
} from "three";
import * as THREE from "three";
import { degToRad } from "three/src/math/MathUtils.js";
import { pageAtom, pages } from "./UI";
import { usePageFlipSound } from "./UI";
import { useTrackButtonClick } from "@/hooks/useTrackButtonClick";


const easingFactor = 0.5;
const easingFactorFold = 0.3;
const insideCurveStrength = 0.18;
const outsideCurveStrength = 0.05;
const turningCurveStrength = 0.09;

const PAGE_WIDTH = 1.28;
const PAGE_HEIGHT = 1.71;
const PAGE_DEPTH = 0.003;
const PAGE_SEGMENTS = 30;
const SEGMENT_WIDTH = PAGE_WIDTH / PAGE_SEGMENTS;

const pageGeometry = new BoxGeometry(PAGE_WIDTH, PAGE_HEIGHT, PAGE_DEPTH, PAGE_SEGMENTS, 2);
pageGeometry.translate(PAGE_WIDTH / 2, 0, 0);

const position = pageGeometry.attributes.position;
const vertex = new Vector3();
const skinIndexes: number[] = [];
const skinWeights: number[] = [];

for (let i = 0; i < position.count; i++) {
    vertex.fromBufferAttribute(position, i);
    const x = vertex.x;
    const skinIndex = Math.max(0, Math.floor(x / SEGMENT_WIDTH));
    const skinWeight = (x % SEGMENT_WIDTH) / SEGMENT_WIDTH;

    skinIndexes.push(skinIndex, skinIndex + 1, 0, 0);
    skinWeights.push(1 - skinWeight, skinWeight, 0, 0);
}

pageGeometry.setAttribute("skinIndex", new Uint16BufferAttribute(skinIndexes, 4));
pageGeometry.setAttribute("skinWeight", new Float32BufferAttribute(skinWeights, 4));

const whiteColor = new Color("white");

const pageMaterials: MeshStandardMaterial[] = [
    new MeshStandardMaterial({ color: whiteColor }),
    new MeshStandardMaterial({ color: "#111" }),
    new MeshStandardMaterial({ color: whiteColor }),
    new MeshStandardMaterial({ color: whiteColor }),
];

// Preload all page textures
pages.forEach((page) => {
    useTexture.preload(`/textures/${page.front}.webp`);
    useTexture.preload(`/textures/${page.back}.webp`);
});

// Props interface for Page component
interface PageProps {
    number: number;
    front: string;
    back: string;
    page: number;
    opened: boolean;
    bookClosed: boolean;
    [key: string]: unknown;
}

export const Page: React.FC<PageProps> = ({
    number,
    front,
    back,
    page,
    opened,
    bookClosed,
    ...props
}) => {
    const textures = useTexture([
        `/textures/${front}.webp`,
        `/textures/${back}.webp`,
    ]) as THREE.Texture[];

    const [picture, picture2] = textures;
    picture.colorSpace = picture2.colorSpace = SRGBColorSpace;

    const group = useRef<THREE.Group>(null!);
    const skinnedMeshRef = useRef<SkinnedMesh>(null!);
    const turnedAt = useRef<number>(0);
    const lastOpened = useRef(opened);

    const manualSkinnedMesh = useMemo(() => {
        const bones: Bone[] = [];
        for (let i = 0; i <= PAGE_SEGMENTS; i++) {
            const bone = new Bone();
            bones.push(bone);
            bone.position.x = i === 0 ? 0 : SEGMENT_WIDTH;
            if (i > 0) bones[i - 1].add(bone);
        }

        const skeleton = new Skeleton(bones);

        const materials: MeshStandardMaterial[] = [
            ...pageMaterials,
            new MeshStandardMaterial({
                color: whiteColor,
                map: picture,

                roughness: 0.1,

                // emissive: emissiveColor,
                // emissiveIntensity: 0,
            }),
            new MeshStandardMaterial({
                color: whiteColor,
                map: picture2,

                roughness: 0.1,

                // emissive: emissiveColor,
                // emissiveIntensity: 1,
            }),
        ];

        const mesh = new SkinnedMesh(pageGeometry, materials);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.frustumCulled = false;
        mesh.add(skeleton.bones[0]);
        mesh.bind(skeleton);

        return mesh;
    }, [picture, picture2, number]);

    const [_, setPage] = useAtom(pageAtom);

    useFrame((_, delta) => {
        if (!skinnedMeshRef.current) return;

        // Assert that materials is an array
        // const materials = skinnedMeshRef.current.material as MeshStandardMaterial[];

        // const emissiveIntensity = highlighted ? 0.22 : 0;
        // materials[4].emissiveIntensity = materials[5].emissiveIntensity = MathUtils.lerp(
        //     materials[4].emissiveIntensity,
        //     emissiveIntensity,
        //     0.1
        // );

        if (lastOpened.current !== opened) {
            turnedAt.current = +new Date();
            lastOpened.current = opened;
        }

        let turningTime = Math.min(400, new Date().getTime() - turnedAt.current) / 400;
        turningTime = Math.sin(turningTime * Math.PI);

        let targetRotation = opened ? -Math.PI / 2 : Math.PI / 2;
        if (!bookClosed) targetRotation += degToRad(number * 0.8);

        const bones = skinnedMeshRef.current.skeleton.bones;
        for (let i = 0; i < bones.length; i++) {
            const target = i === 0 ? group.current! : bones[i];

            const insideCurveIntensity = i < 8 ? Math.sin(i * 0.2 + 0.25) : 0;
            const outsideCurveIntensity = i >= 8 ? Math.cos(i * 0.3 + 0.09) : 0;
            const turningIntensity = Math.sin(i * Math.PI / bones.length) * turningTime;

            let rotationAngle =
                insideCurveStrength * insideCurveIntensity * targetRotation -
                outsideCurveStrength * outsideCurveIntensity * targetRotation +
                turningCurveStrength * turningIntensity * targetRotation;

            let foldRotationAngle = degToRad(Math.sign(targetRotation) * 2);
            if (bookClosed) {
                rotationAngle = i === 0 ? targetRotation : 0;
                foldRotationAngle = 0;
            }

            easing.dampAngle(target.rotation, "y", rotationAngle, easingFactor, delta);

            const foldIntensity = i > 8 ? Math.sin(i * Math.PI / bones.length - 0.5) * turningTime : 0;
            easing.dampAngle(target.rotation, "x", foldRotationAngle * foldIntensity, easingFactorFold, delta);
        }
    });

    // ✅ Hook for sound
    const playSound = usePageFlipSound();

    const trackClick = useTrackButtonClick();

    return (
        <group
            {...props}
            ref={group}
            onClick={(e) => {
                e.stopPropagation();
                // Track GA
                trackClick(`Book Page ${number + 1}`);

                setPage(opened ? number : number + 1);
                playSound(); // 👈 play sound here
            }}
        >
            <primitive
                object={manualSkinnedMesh}
                ref={skinnedMeshRef}
                position-z={-number * PAGE_DEPTH + page * PAGE_DEPTH}
            />
        </group>
    );
};

// Props for Book component
interface BookProps {
    [key: string]: unknown;
}

export const Book: React.FC<BookProps> = ({ ...props }) => {
    const [page] = useAtom(pageAtom);
    const [delayedPage, setDelayedPage] = useState(page);

    useEffect(() => {
        let timeout: NodeJS.Timeout;
        const goToPage = () => {
            setDelayedPage((delayedPage) => {
                if (page === delayedPage) return delayedPage;

                timeout = setTimeout(goToPage, Math.abs(page - delayedPage) > 2 ? 50 : 150);
                return page > delayedPage ? delayedPage + 1 : delayedPage - 1;
            });
        };
        goToPage();
        return () => clearTimeout(timeout);
    }, [page]);

    return (
        <group {...props} rotation-y={-Math.PI / 2}>
            {pages.map((pageData, index) => (
                <Page
                    key={index}
                    page={delayedPage}
                    number={index}
                    opened={delayedPage > index}
                    bookClosed={delayedPage === 0 || delayedPage === pages.length}
                    {...pageData}
                />
            ))}
        </group>
    );
};
